import React, { useEffect, useRef, useState } from "react";
import { Scissors, Upload, Library, Video, Link2, Loader2, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * GreenScreenToggle — compact chroma-key + virtual-background controls.
 *
 * Publishes a `settings` shape compatible with `useFxCompositor`:
 *
 *   {
 *     chroma:     { enabled, color, similarity, smoothness },
 *     background: {
 *       mode: "none"|"blur"|"image"|"video",
 *       dataUrl?: string,   // base64 (device upload, ≤10MB)
 *       url?:     string,   // remote URL (admin library / pasted link)
 *     },
 *     scope: { main: true },
 *   }
 *
 * Parents own the localStorage persistence — different call-sites use
 * different keys (`tapin_host_greenscreen`, `tapin_guest_greenscreen`).
 */
const MAX_VIDEO_SEC = 30;
const MAX_UPLOAD_MB = 10;
// Per-device recently-used backgrounds — capped to 12 most-recent so the
// rail never grows past one row on most viewports. Stored as the raw
// library-item shape so we can render thumbs without a re-fetch.
const RECENTS_KEY = "tapin_greenscreen_recents";
const RECENTS_MAX = 12;

function readRecents() {
  try {
    const arr = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
    return Array.isArray(arr) ? arr.slice(0, RECENTS_MAX) : [];
  } catch { return []; }
}

function pushRecent(item) {
  if (!item || !item.id) return;
  try {
    const cur = readRecents().filter((r) => r.id !== item.id);
    cur.unshift({
      id: item.id,
      title: item.title,
      kind: item.kind,
      data_url: item.data_url || "",
      url: item.url || "",
    });
    localStorage.setItem(RECENTS_KEY, JSON.stringify(cur.slice(0, RECENTS_MAX)));
  } catch { /* quota — silent fail */ }
}

export default function GreenScreenToggle({
  value,
  onChange,
  testidPrefix = "green-screen",
  compact = false,
}) {
  const fileRef = useRef(null);
  const [, forceRender] = useState(0);
  const [library, setLibrary] = useState(null); // null = not fetched yet
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Search / filter inside the library grid — only rendered once the
  // library has >=10 items so short collections stay tidy.
  const [libQuery, setLibQuery] = useState("");
  const [libKind, setLibKind] = useState("all"); // all | image | video
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  // Per-device "recently used" backgrounds. We track these client-side
  // (no backend) so individual creators see their own usage history
  // without a per-user API roundtrip on every modal open.
  const [recents, setRecents] = useState(readRecents);

  // Hydrate missing fields so legacy persisted objects don't crash the UI.
  useEffect(() => {
    if (!value) return;
    const needsPatch =
      !value.chroma || !value.background || !value.scope ||
      typeof value.chroma.enabled !== "boolean";
    if (needsPatch) {
      onChange?.({
        chroma: {
          enabled: !!value.chroma?.enabled,
          color: value.chroma?.color || "#00FF00",
          similarity: value.chroma?.similarity ?? 0.4,
          smoothness: value.chroma?.smoothness ?? 0.1,
        },
        background: {
          mode: value.background?.mode || "none",
          dataUrl: value.background?.dataUrl || "",
          url: value.background?.url || "",
        },
        scope: { main: true },
      });
    }
  }, [value, onChange]);

  const v = value || {
    chroma: { enabled: false, color: "#00FF00", similarity: 0.4, smoothness: 0.1 },
    background: { mode: "none", dataUrl: "", url: "" },
    scope: { main: true },
  };

  const patchBg = (next) => {
    onChange?.({ ...v, background: { ...v.background, ...next } });
    forceRender((x) => x + 1);
  };
  const patch = (path, val) => {
    const [a, b] = path.split(".");
    onChange?.({ ...v, [a]: { ...v[a], [b]: val } });
    forceRender((x) => x + 1);
  };

  // Fetch the admin-curated library lazily on first open.
  useEffect(() => {
    if (!libraryOpen || library !== null) return;
    api.get("/greenscreen/backgrounds")
      .then(({ data }) => setLibrary(data?.items || []))
      .catch(() => setLibrary([]));
  }, [libraryOpen, library]);

  // Hydrate per-user recents from the server on mount — merges with the
  // existing localStorage list so recents follow the creator across
  // devices without wiping their local history.
  useEffect(() => {
    let cancelled = false;
    api.get("/greenscreen/my-recents")
      .then(({ data }) => {
        if (cancelled) return;
        const serverItems = Array.isArray(data?.items) ? data.items : [];
        if (!serverItems.length) return;
        // Merge: local first (most-recent on this device wins for ties),
        // then add any server entries we haven't seen. Cap at 12.
        const local = readRecents();
        const seen = new Set(local.map((r) => r.id));
        const merged = [
          ...local,
          ...serverItems.filter((r) => r && r.id && !seen.has(r.id)),
        ].slice(0, RECENTS_MAX);
        try { localStorage.setItem(RECENTS_KEY, JSON.stringify(merged)); } catch { /* quota */ }
        setRecents(merged);
      })
      .catch(() => { /* silent — device-local still works */ });
    return () => { cancelled = true; };
  }, []);

  /**
   * Handle an image OR video picked from the device. Videos are
   * client-measured to enforce the 30s ceiling BEFORE base64-encoding
   * (otherwise a 90s phone clip would chew through tens of megabytes
   * for nothing). We cap the file size at 10MB.
   */
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isVid = f.type.startsWith("video/");
    if (!isImg && !isVid) {
      toast.error("Pick an image or video file");
      return;
    }
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_UPLOAD_MB}MB — host externally + paste a link`);
      return;
    }
    setUploading(true);
    try {
      if (isVid) {
        const duration = await probeVideoDuration(f);
        if (!Number.isFinite(duration) || duration <= 0) throw new Error("Could not read video");
        if (duration > MAX_VIDEO_SEC + 0.5) {
          toast.error(`Video is ${duration.toFixed(1)}s — max ${MAX_VIDEO_SEC}s allowed`);
          return;
        }
      }
      const dataUrl = await readAsDataUrl(f);
      patchBg({
        mode: isVid ? "video" : "image",
        dataUrl,
        url: "",
      });
      toast.success(isVid ? "Video background set" : "Image background set");
    } catch (err) {
      toast.error("Couldn't load that file");
    } finally {
      setUploading(false);
    }
  };

  const applyLink = () => {
    const u = (linkUrl || "").trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error("Paste a http / https URL");
      return;
    }
    // Heuristic — decide image vs video by extension. Fall back to image
    // which is the safer default (videos that render as `<img>` just
    // show a broken icon vs. an image loaded via <video> renders
    // nothing silently).
    const lower = u.split("?")[0].toLowerCase();
    const isVideo = /\.(mp4|webm|mov|m4v|ogv)$/i.test(lower);
    patchBg({ mode: isVideo ? "video" : "image", url: u, dataUrl: "" });
    toast.success(`${isVideo ? "Video" : "Image"} link applied`);
    setLinkOpen(false);
    setLinkUrl("");
  };

  const applyLibraryItem = (it) => {
    const src = it.data_url || it.url || "";
    if (!src) return;
    patchBg({ mode: it.kind === "video" ? "video" : "image", dataUrl: it.data_url || "", url: it.url || "" });
    pushRecent(it);
    const next = readRecents();
    setRecents(next);
    // Best-effort cross-device sync — server hard-caps and strips
    // data_urls, so we can safely fire-and-forget.
    api.post("/greenscreen/my-recents", { items: next.map((r) => ({ id: r.id, title: r.title, kind: r.kind, url: r.url })) }).catch(() => {});
    toast.success(`${it.title} applied`);
  };

  const clearRecents = () => {
    try { localStorage.removeItem(RECENTS_KEY); } catch { /* quota */ }
    setRecents([]);
    api.post("/greenscreen/my-recents", { items: [] }).catch(() => {});
  };

  const clearBg = () => {
    patchBg({ mode: "none", dataUrl: "", url: "" });
  };

  const currentBgLabel = (() => {
    if (v.background?.mode === "blur") return "Camera blur";
    if (v.background?.mode === "image") return "Image background";
    if (v.background?.mode === "video") return "Video background";
    return "No background";
  })();

  return (
    <div
      data-testid={`${testidPrefix}-panel`}
      className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scissors className="w-4 h-4 text-tv-accent" />
          <div className="text-white font-black text-xs uppercase tracking-wider">
            Green-screen mode
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`${testidPrefix}-enable`}
            checked={!!v.chroma.enabled}
            onChange={(e) => patch("chroma.enabled", e.target.checked)}
            className="peer sr-only"
          />
          <span className="w-9 h-5 bg-white/15 rounded-full relative transition peer-checked:bg-tv-accent">
            <span
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition ${
                v.chroma.enabled ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </span>
        </label>
      </div>

      {v.chroma.enabled && (
        <>
          {/* Recently-used rail — only renders when the host has applied
              ≥1 library background on this device. Clicking re-applies
              with one tap, and bumps the item back to the front. */}
          {recents.length > 0 && (
            <div data-testid={`${testidPrefix}-recents`} className="space-y-1">
              <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-white/50 font-bold">
                <span className="inline-flex items-center gap-1">
                  <Star className="w-3 h-3 text-tv-accent" /> Recently used
                </span>
                <button
                  type="button"
                  onClick={clearRecents}
                  data-testid={`${testidPrefix}-recents-clear`}
                  className="text-white/40 hover:text-white normal-case tracking-normal"
                  title="Clear recently used"
                >
                  Clear
                </button>
              </div>
              <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
                {recents.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => applyLibraryItem(it)}
                    data-testid={`${testidPrefix}-recents-item-${it.id}`}
                    title={it.title}
                    className="relative shrink-0 w-14 aspect-video rounded border border-white/10 hover:border-tv-accent overflow-hidden bg-black"
                  >
                    {it.kind === "video" ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-900/50 to-indigo-900/50">
                        <Video className="w-3 h-3 text-white/80" />
                      </div>
                    ) : (
                      <img src={it.data_url || it.url} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Background mode row — none / blur / image / video */}
          <div className="grid grid-cols-4 gap-1 text-[10px]">
            {[
              { k: "none", lbl: "None" },
              { k: "blur", lbl: "Blur" },
              { k: "image", lbl: "Image" },
              { k: "video", lbl: "Video" },
            ].map(({ k, lbl }) => (
              <button
                key={k}
                type="button"
                data-testid={`${testidPrefix}-bg-${k}`}
                onClick={() => patchBg({ mode: k })}
                className={`px-1.5 py-1 rounded font-bold uppercase tracking-wider ${
                  v.background.mode === k
                    ? "bg-tv-accent text-black"
                    : "bg-white/10 hover:bg-white/15 text-white/70"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>

          {/* Source row — upload / link / library. Visible only when
              the selected mode needs an asset (image or video). */}
          {(v.background.mode === "image" || v.background.mode === "video") && (
            <div className="grid grid-cols-3 gap-1 text-[10px]">
              <button
                type="button"
                disabled={uploading}
                data-testid={`${testidPrefix}-bg-upload`}
                onClick={() => fileRef.current?.click()}
                title={`Upload from device — images + videos ≤${MAX_VIDEO_SEC}s / ${MAX_UPLOAD_MB}MB`}
                className="flex items-center justify-center gap-1 px-1.5 py-1 rounded bg-white/10 hover:bg-white/15 text-white/80 font-bold uppercase tracking-wider disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                Upload
              </button>
              <button
                type="button"
                data-testid={`${testidPrefix}-bg-link-toggle`}
                onClick={() => { setLinkOpen((x) => !x); setLibraryOpen(false); }}
                title="Paste a direct image / video URL"
                className={`flex items-center justify-center gap-1 px-1.5 py-1 rounded font-bold uppercase tracking-wider ${
                  linkOpen ? "bg-white/25 text-white" : "bg-white/10 hover:bg-white/15 text-white/80"
                }`}
              >
                <Link2 className="w-3 h-3" />
                Link
              </button>
              <button
                type="button"
                data-testid={`${testidPrefix}-bg-library-toggle`}
                onClick={() => { setLibraryOpen((x) => !x); setLinkOpen(false); }}
                title="Browse the admin-curated library"
                className={`flex items-center justify-center gap-1 px-1.5 py-1 rounded font-bold uppercase tracking-wider ${
                  libraryOpen ? "bg-white/25 text-white" : "bg-white/10 hover:bg-white/15 text-white/80"
                }`}
              >
                <Library className="w-3 h-3" />
                Library
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                onChange={onUpload}
                className="hidden"
                data-testid={`${testidPrefix}-bg-upload-input`}
              />
            </div>
          )}

          {/* Pasted-link input */}
          {linkOpen && (
            <div className="flex gap-1" data-testid={`${testidPrefix}-bg-link-row`}>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyLink()}
                placeholder="https://… (image or mp4)"
                data-testid={`${testidPrefix}-bg-link-input`}
                className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white outline-none focus:border-tv-accent"
              />
              <button
                type="button"
                data-testid={`${testidPrefix}-bg-link-apply`}
                onClick={applyLink}
                className="px-2 py-1 rounded bg-tv-accent text-black text-[10px] font-black uppercase"
              >
                Set
              </button>
            </div>
          )}

          {/* Library grid */}
          {libraryOpen && (
            <div data-testid={`${testidPrefix}-bg-library`} className="space-y-1">
              {library === null && (
                <div className="text-[10px] text-white/50 italic flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading library…
                </div>
              )}
              {library !== null && library.length === 0 && (
                <div className="text-[10px] text-white/50 italic" data-testid={`${testidPrefix}-bg-library-empty`}>
                  Library is empty — ask an admin to upload backgrounds.
                </div>
              )}
              {library !== null && library.length > 0 && (
                <>
                  {/* Search + kind filter — only render past 10 items so
                      small collections aren't cluttered. */}
                  {library.length >= 10 && (
                    <div className="flex items-center gap-1" data-testid={`${testidPrefix}-bg-library-controls`}>
                      <input
                        type="search"
                        value={libQuery}
                        onChange={(e) => setLibQuery(e.target.value)}
                        placeholder="Search library…"
                        data-testid={`${testidPrefix}-bg-library-search`}
                        className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white outline-none focus:border-tv-accent"
                      />
                      <div className="flex gap-0.5 text-[9px]">
                        {["all", "image", "video"].map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setLibKind(k)}
                            data-testid={`${testidPrefix}-bg-library-kind-${k}`}
                            className={`px-1.5 py-1 rounded font-black uppercase tracking-wider ${
                              libKind === k ? "bg-tv-accent text-black" : "bg-white/10 hover:bg-white/15 text-white/70"
                            }`}
                          >
                            {k === "all" ? "All" : k}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {(() => {
                    const q = libQuery.trim().toLowerCase();
                    const filtered = library.filter((it) => {
                      if (libKind !== "all" && it.kind !== libKind) return false;
                      if (!q) return true;
                      return (it.title || "").toLowerCase().includes(q);
                    });
                    if (filtered.length === 0) {
                      return (
                        <div className="text-[10px] text-white/40 italic" data-testid={`${testidPrefix}-bg-library-noresults`}>
                          No backgrounds match — try a different search.
                        </div>
                      );
                    }
                    return (
                      <div className="grid grid-cols-3 gap-1 max-h-32 overflow-y-auto pr-1">
                        {filtered.map((it) => (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => applyLibraryItem(it)}
                            data-testid={`${testidPrefix}-bg-library-item-${it.id}`}
                            title={`${it.title}${it.kind === "video" ? ` · ${Math.round((it.duration_ms || 0) / 1000)}s` : ""}`}
                            className="relative aspect-video rounded border border-white/10 hover:border-tv-accent overflow-hidden bg-black"
                          >
                            {it.kind === "video" ? (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-900/50 to-indigo-900/50">
                                <Video className="w-4 h-4 text-white/80" />
                              </div>
                            ) : (
                              <img
                                src={it.data_url || it.url}
                                alt={it.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            )}
                            <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 truncate">
                              {it.title}
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Current-bg preview + clear */}
          {(v.background.mode === "image" || v.background.mode === "video") && (v.background.dataUrl || v.background.url) && (
            <div
              className="relative rounded overflow-hidden h-16 bg-black/40 border border-white/10"
              data-testid={`${testidPrefix}-bg-preview`}
            >
              {v.background.mode === "video" ? (
                <video
                  src={v.background.dataUrl || v.background.url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={v.background.dataUrl || v.background.url}
                  alt="bg preview"
                  className="w-full h-full object-cover"
                />
              )}
              <button
                type="button"
                data-testid={`${testidPrefix}-bg-clear`}
                onClick={clearBg}
                title="Clear background"
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded p-1"
              >
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 rounded">
                {currentBgLabel}
              </div>
            </div>
          )}

          {!compact && (
            <>
              <div className="flex items-center gap-2 text-[11px] text-white/70">
                <label>Key color</label>
                <input
                  type="color"
                  data-testid={`${testidPrefix}-color`}
                  value={v.chroma.color}
                  onChange={(e) => patch("chroma.color", e.target.value)}
                  className="w-7 h-7 rounded cursor-pointer bg-transparent border border-white/20"
                />
                <span className="ml-auto font-mono text-[10px]">{v.chroma.color.toUpperCase()}</span>
              </div>
              <Slider
                label="Similarity"
                testid={`${testidPrefix}-similarity`}
                value={v.chroma.similarity}
                onChange={(val) => patch("chroma.similarity", val)}
              />
              <Slider
                label="Smoothness"
                testid={`${testidPrefix}-smoothness`}
                value={v.chroma.smoothness}
                onChange={(val) => patch("chroma.smoothness", val)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function Slider({ label, testid, value, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-white/50 font-bold">
        <span>{label}</span>
        <span className="tabular-nums">{Math.round(value * 100)}%</span>
      </div>
      <input
        type="range" min="0" max="1" step="0.02"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="w-full accent-tv-accent"
      />
    </div>
  );
}

/** Read a File as a data URL (resolves with the base64 string). */
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}

/** Resolve the duration (seconds) of a video File by creating a throw-
 *  away hidden <video> — browsers fire `loadedmetadata` with the real
 *  duration before any frames download (covers 99% of cases, iOS can
 *  be finicky which is why we fall back to 0 on failure). */
function probeVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    const blob = URL.createObjectURL(file);
    v.preload = "metadata";
    v.muted = true;
    v.src = blob;
    const done = (d) => { URL.revokeObjectURL(blob); resolve(d); };
    v.onloadedmetadata = () => done(v.duration);
    v.onerror = () => { URL.revokeObjectURL(blob); reject(v.error); };
    setTimeout(() => done(0), 4000); // safety net
  });
}
